import { Divider, Flex, Group, Select, Switch, Text } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import { BlockType, EVENT_VARIABLE_TYPE } from '@lighthouse/core'
import {
    Group4ByecodeUi,
    ListItem4ByecodeUi,
    SelectDataSource,
    useAtomAction,
    useAtomData,
    useSharedConfigDisabledWithVersion,
    VariableSelect
} from '@lighthouse/shared'
import { stopPropagation } from '@lighthouse/tools'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { pageStackOfFormContainerBlockChangedAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { CollectionEvent } from '@/components/CollectionEvent'
import { EventList } from '@/components/EventList'
import { InheritLabel } from '@/components/InheritLabel'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationController from '@/components/StyleSetting/AnimationSetting'
import { AutoLayoutSetting } from '@/components/StyleSetting/AutoLayoutSetting'
import { DesignSetting } from '@/components/StyleSetting/DesignSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { ValidationRuleAdder } from '@/components/ValidationRuleAdder'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { WeChatCollector } from '@/components/WeChatCollector'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useNotAggregateDataSourceList } from '@/hooks/useDataSource'
import { useCurrentLanguagePageList, useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { ButtonItemConfigure } from '../Button/ButtonItemConfigure'
import { BUTTON_RADIUS_OPTIONS, BUTTON_SIZE_OPTIONS } from '../Button/constant'
import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { FloatingControl } from '../Common/FloatingControl'
import FormSegmentedControl from '../Common/FormSegmentedControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM, POSITION_ALIGN_CONTROL } from '../constants'
import type { ContainerFormSlice } from './formSlice'
import { ViewListSettings } from './ViewListSettings'

interface ContainerSettingProps {
    type: Extract<BlockType, 'container' | 'formContainer' | 'tabsContainer'>
    containerConfigureLoading?: boolean
}

const formEventTypes = [EVENT_VARIABLE_TYPE.BUTTON_CLICK]

const FORM_TYPE_OPTIONS = [
    { label: '创建记录', value: 'CREATE' },
    { label: '更新记录', value: 'UPDATE' }
]

const UPDATE_TYPE_OPTIONS = [
    { label: '通过提交按钮提交更新', value: 'SUBMIT' },
    { label: '单个输入框实时更新', value: 'REALTIME' }
]
const SCxRequiredLabel = styled(Text)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    &.required {
        &::after {
            content: '*';
            margin-left: 4px;
            color: var(--color-red-500);
        }
    }
`
const textField: Field = {
    type: 'text',
    innerType: 'TEXT',
    id: 'text',
    name: '文本',
    dsId: ''
}
export const ContainerSetting: React.FC<ContainerSettingProps> = ({ type, containerConfigureLoading }) => {
    const { watch, control } = useFormContext<ContainerFormSlice>()
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const styleProps = getBlockAndPageDesignLimit(type)
    const isFormContainer = type === BlockType.formContainer
    const allPages = useCurrentLanguagePageList()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useNotAggregateDataSourceList(appId, envId)
    const disabledWithVersion = useSharedConfigDisabledWithVersion()
    const { blockId } = useBlockSettingContext()

    const { run: setPageStackOfFormContainerBlockChanged } = useAtomAction(pageStackOfFormContainerBlockChangedAtom)

    const [formPointer, submitType, updateType] = watch(['pointer', 'submitType', 'updateType'])

    const formModuleDs = useMemo(
        () => ({
            formDsId: formPointer || ''
        }),
        [formPointer]
    )

    const [pageId, stackId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.pageId || '', s?.stackId || ''], [])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    const { customViewOption } = useVariableCustomViewOption()

    useEffect(() => {
        if (submitType === 'UPDATE' && updateType === 'REALTIME') {
            setPageStackOfFormContainerBlockChanged(draft => {
                draft[stackId] = false
            })
        }
    }, [submitType, updateType, stackId, setPageStackOfFormContainerBlockChanged])

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />
            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    {!isFormContainer && (
                        <>
                            <Group
                                label={
                                    <PopoverQuestion
                                        type="panel"
                                        renderTarget={(node, ref) => (
                                            <Flex gap={4} alignItems="center" ref={ref} onClick={stopPropagation}>
                                                <InheritLabel label="面板" name="breakPoint.panel.defaultPanel" />
                                                {node}
                                            </Flex>
                                        )}
                                    />
                                }
                            >
                                <ViewListSettings containerConfigureLoading={containerConfigureLoading} />
                            </Group>
                            <Divider />
                        </>
                    )}
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <AutoLayoutSetting {...styleProps.layout} />
                    <Divider />
                    <Group label="样式">
                        <DesignSetting {...styleProps.design} />
                    </Group>
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && (
                <>
                    <Controller
                        control={control}
                        name="eventList"
                        render={({ field }) => (
                            <EventList blockType={type} form={formModuleDs} value={field.value} onChange={field.onChange} />
                        )}
                    />
                    <Divider color="var(--color-gray-200)" />
                    {!isFormContainer && (
                        <>
                            <Controller
                                name="floatPointerList"
                                control={control}
                                render={({ field }) => {
                                    return <FloatingControl value={field.value} blockId={blockId} onChange={field.onChange} />
                                }}
                            />
                            <Divider />
                        </>
                    )}
                    <AnimationController />
                </>
            )}
            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    {isFormContainer && (
                        <>
                            <Group4ByecodeUi label="表单">
                                <Controller
                                    name="submitType"
                                    control={control}
                                    render={({ field }) => (
                                        <FormSegmentedControl
                                            label="表单场景"
                                            size="md"
                                            data={FORM_TYPE_OPTIONS}
                                            value={field.value}
                                            onChange={v => field.onChange(v)}
                                        />
                                    )}
                                />
                                {submitType === 'CREATE' && (
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <SCxRequiredLabel className="required">提交数据到</SCxRequiredLabel>
                                        <Controller
                                            name="pointer"
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <SelectDataSource
                                                    withinPortal
                                                    placeholder="请选择数据表"
                                                    value={value}
                                                    onChange={onChange}
                                                    // 表单编辑页可以使用用户表
                                                    dataSourceList={dataSourceList}
                                                />
                                            )}
                                        />
                                    </ListItem4ByecodeUi>
                                )}
                                {submitType === 'UPDATE' && (
                                    <>
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <SCxRequiredLabel>更新方式</SCxRequiredLabel>
                                            <Controller
                                                name="updateType"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Select
                                                        value={value}
                                                        style={{ width: 180 }}
                                                        onChange={onChange}
                                                        position="bottom-end"
                                                        options={UPDATE_TYPE_OPTIONS}
                                                    />
                                                )}
                                            />
                                        </ListItem4ByecodeUi>
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <SCxRequiredLabel className="required">记录来自</SCxRequiredLabel>
                                            <Controller
                                                name="pointer"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <SelectDataSource
                                                        withinPortal
                                                        placeholder="请选择数据表"
                                                        value={value}
                                                        disabled={disabledWithVersion}
                                                        onChange={onChange}
                                                        // 表单编辑页可以使用用户表
                                                        dataSourceList={dataSourceList}
                                                    />
                                                )}
                                            />
                                        </ListItem4ByecodeUi>
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <SCxRequiredLabel>记录编号</SCxRequiredLabel>
                                            <Controller
                                                name="recordId"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <VariableSelect
                                                        innerTypes={[textField.innerType]}
                                                        placeholder="请输入"
                                                        field={textField}
                                                        value={value}
                                                        disablePexels
                                                        onChange={onChange}
                                                        viewOptions={customViewOption ? [customViewOption] : undefined}
                                                        pageOptions={pageOptions}
                                                        userOption={userOption}
                                                        style={{ width: 180 }}
                                                    />
                                                )}
                                            />
                                        </ListItem4ByecodeUi>
                                    </>
                                )}
                            </Group4ByecodeUi>
                            <Divider color="var(--color-gray-200)" />
                            {(updateType === 'SUBMIT' || submitType === 'CREATE') && (
                                <>
                                    <Group label="提交按钮">
                                        <ButtonItemConfigure
                                            prefixName="submitButton"
                                            style={{ padding: 0 }}
                                            hideConfigs={{ fillWay: true }}
                                            // themeColor="primaryColor"
                                        />
                                        <Controller
                                            name="submitButton.size"
                                            control={control}
                                            render={({ field }) => (
                                                <FormSegmentedControl
                                                    label="尺寸"
                                                    size="md"
                                                    data={BUTTON_SIZE_OPTIONS}
                                                    value={field.value}
                                                    onChange={v => field.onChange(v)}
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="submitButton.radius"
                                            control={control}
                                            render={({ field }) => (
                                                <FormSegmentedControl
                                                    label="圆角"
                                                    size="md"
                                                    data={BUTTON_RADIUS_OPTIONS}
                                                    value={field.value}
                                                    onChange={v => field.onChange(v)}
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="submitButton.align"
                                            control={control}
                                            render={({ field }) => (
                                                <FormSegmentedControl
                                                    label="对齐"
                                                    size="md"
                                                    data={POSITION_ALIGN_CONTROL}
                                                    value={field.value}
                                                    onChange={v => field.onChange(v)}
                                                />
                                            )}
                                        />
                                    </Group>
                                    <Divider />

                                    <Controller
                                        control={control}
                                        name="collectEvents"
                                        render={({ field }) => (
                                            <CollectionEvent eventTypes={formEventTypes} value={field.value} onChange={field.onChange} />
                                        )}
                                    />
                                    <Divider color="var(--color-gray-200)" />
                                    <Group label="自定义校验规则">
                                        <ValidationRuleAdder />
                                    </Group>
                                    <Divider color="var(--color-gray-200)" />
                                    <Group label="自动采集">
                                        <WeChatCollector />
                                    </Group>
                                    <Divider color="var(--color-gray-200)" />
                                </>
                            )}
                        </>
                    )}
                    {!isFormContainer && (
                        <>
                            <Group label="其他">
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <div>展开容器</div>
                                    <Controller
                                        control={control}
                                        name="shown"
                                        render={({ field }) => (
                                            <Switch checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <div>允许横向拖拽宽度</div>
                                    <Controller
                                        control={control}
                                        name="allowResizeWidth"
                                        render={({ field }) => (
                                            <Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <div>允许纵向拖拽高度</div>
                                    <Controller
                                        control={control}
                                        name="allowResizeHeight"
                                        render={({ field }) => (
                                            <Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                            </Group>
                            <Divider color="var(--color-gray-200)" />
                        </>
                    )}
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}
        </>
    )
}
